import * as IoC from 'typescript-ioc';
import React from 'react';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { action, observable } from 'mobx';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { ADD_CONNECTION_GROUP_TITLE_TEXT, DELETE_GROUP_TEXT, EDIT_CONNECTION_GROUP_TITLE_TEXT } from './connection-groups.texts';
import { ConnectionGroupForm } from './connection-group-from/connection-group-form';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from '../../../../../api/connections.dao';
import Text from '../../../../global/text/text';
import { Space } from '../../../../../constants/html-character-entities';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { ConnectionGroupsPassportPreviewViewModel } from './connection-groups-passport-preview/connection-group-passport-preview.viewmodel';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { PassportConfigurationMode } from '../configure-passport-display/configure-passport-display.config';


export class ConnectionGroupsListVM {
    @observable
    private newConnectionGroupSubWindow: ISubWindowFacade | null = null;
    @observable
    private groupIdToEditPopupMap: { [key: number]: ISubWindowFacade } = {};
    @observable
    public isPassportPreviewModalOpen: boolean;
    constructor(
        private readonly getGroups: () => IConnectionGroupDTO[],
        private readonly getConnections: () => IConnectionDetailsDTO[],
        private readonly contextParams: { meetingId?: number; roomId?: number },
        private readonly createConnectionGroup: (connectionGroup: IConnectionGroupDTO) => Promise<IConnectionGroupDTO>,
        private readonly editConnectionGroup: (connectionGroup: IConnectionGroupDTO) => Promise<IConnectionGroupDTO>,
        private readonly deleteConnectionGroup: (groupId: number) => Promise<void>,
        public readonly getDefaultConnectionId?: () => number | null,
        public readonly updateDefaultConnection?: (meetingConnectionId: number | null) => void,
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {}

    public get connections(): IConnectionDetailsDTO[] {
        return this.getConnections();
    }

    public get isMeetingConnectionForm(): boolean {
        return !!this.contextParams.meetingId;
    }

    public get isEditRoomForm(): boolean {
        return !!this.contextParams.roomId;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isNewGroupPopupOpened(): boolean {
        return !!this.newConnectionGroupSubWindow && this.newConnectionGroupSubWindow.getStatus() !== SubWindowStatus.Closed;
    }

    public get groupList(): IConnectionGroupDTO[] {
        return this.getGroups();
    }

    @action.bound
    public openAddConnectionGroupPopup(): void {
        this.newConnectionGroupSubWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(ADD_CONNECTION_GROUP_TITLE_TEXT),
            componentToHost: ConnectionGroupForm,
            componentToHostProps: {
                connections: this.getConnections(),
                contextParams: this.contextParams,
                formSubmitAction: this.createConnectionGroup
            }
        });
    }

    public onEditGroup = (group: IConnectionGroupDTO): void => {
        this.openEditGroupPopup(group);
    };

    @action
    public openEditGroupPopup = (group: IConnectionGroupDTO): void => {
        if (this.isGroupPopupOpen(group.meetingConnectionId)) {
            this.groupIdToEditPopupMap[group.meetingConnectionId].maximize();
        } else {
            this.groupIdToEditPopupMap[group.meetingConnectionId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(EDIT_CONNECTION_GROUP_TITLE_TEXT),
                componentToHost: ConnectionGroupForm,
                componentToHostProps: {
                    connections: this.getConnections(),
                    contextParams: this.contextParams,
                    formSubmitAction: this.editConnectionGroup,
                    deleteConnectionGroup: this.onDeleteConnectionGroup,
                    group: group
                }
            });
        }
    };

    @action
    public getOrderedConnections = (group: IConnectionGroupDTO): IConnectionDetailsDTO[] => {
        const connections = this.getConnections();
        return group.joinMethods
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(({ joinMethodId }) => connections.find(c => c.joinMethodId === joinMethodId))
            .filter(Boolean)
        ;
    };

    public isGroupPopupOpen = (groupId: number): boolean => {
        const alreadyShownSubWindow = this.groupIdToEditPopupMap[groupId];
        return alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed;
    };

    @action
    public onDeleteConnectionGroup = (groupId: number): void => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.connectionGroups.deleteThisConnectionsGroup'
                        description='Alert message'
                        message='delete this Connection Group?'
                    /><br /><br />
                    <Text
                        className='alert-message'
                        id='oe.connectionGroups.deletedGroupWillBeUnassignedFromAllAttendees.'
                        description='Alert message'
                        message='The deleted group will be unassigned from all Attendees. The Connections in the Group will not be deleted.'
                    /><br />
                </>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage(DELETE_GROUP_TEXT),
                modifier: 'confirm-warn',
                handler: () => this.handleDeleteConnectionGroup(groupId),
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @action
    public setPassportPreviewOpen = (value: boolean): void => {
        this.isPassportPreviewModalOpen = value;
    };

    public getPassportPreviewVM = (group: IConnectionGroupDTO): ConnectionGroupsPassportPreviewViewModel => {
        const orderedConnections = this.getOrderedConnections(group);
        return new ConnectionGroupsPassportPreviewViewModel(
            this.contextParams,
            orderedConnections,
            group.label,
            PassportConfigurationMode.MeetingConnection
        );
    };

    @action
    private handleDeleteConnectionGroup = async (groupId: number): Promise<void> => {
        this.groupIdToEditPopupMap[groupId]?.close();
        await this.deleteConnectionGroup(groupId);
    };
}
